import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { PhoneInput } from '@/components/ui/PhoneInput';

interface AuthInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
}

export const AuthInput = forwardRef<HTMLInputElement, AuthInputProps>(
  ({ label, error, className = '', id, type = 'text', value, onChange, onBlur, name, ...props }, ref) => {
    const inputId = id ?? name ?? label.toLowerCase().replace(/\s+/g, '-');
    const isPassword = type === 'password';
    const [showPassword, setShowPassword] = useState(false);
    const inputType = isPassword && showPassword ? 'text' : type;

    if (type === 'tel') {
      return (
        <PhoneInput
          ref={ref}
          label={label}
          error={error}
          className={className}
          id={inputId}
          name={name}
          value={typeof value === 'string' ? value : ''}
          onChange={onChange}
          onBlur={onBlur}
          disabled={props.disabled}
          required={props.required}
          autoFocus={props.autoFocus}
          placeholder={props.placeholder}
          onKeyDown={props.onKeyDown}
        />
      );
    }

    return (
      <div>
        <label htmlFor={inputId} className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
          {label}
        </label>
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            name={name}
            type={inputType}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            className={`w-full rounded-xl border bg-white py-2.5 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:bg-slate-800 dark:text-slate-100 ${
              isPassword ? 'pr-11' : ''
            } px-4 ${
              error
                ? 'border-red-400 dark:border-red-500'
                : 'border-slate-200 dark:border-slate-600'
            } ${className}`}
            {...props}
          />
          {isPassword && (
            <button
              type="button"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              onClick={() => setShowPassword((v) => !v)}
            >
              {showPassword ? (
                <VisibilityOffIcon sx={{ fontSize: 20 }} />
              ) : (
                <VisibilityIcon sx={{ fontSize: 20 }} />
              )}
            </button>
          )}
        </div>
        {error ? <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p> : null}
      </div>
    );
  },
);
AuthInput.displayName = 'AuthInput';
